/**
 * 소유자 전용 유니크 ID.
 *
 * `RESERVED_HANDLES`(예약어)와 달리, 이 ID는 **특정 계정에만 허용**된다.
 * 예약어로 넣으면 소유자 자신도 쓸 수 없어서 목적과 반대된다.
 *
 * 값은 소스에 박지 않는다 — 배포 환경변수에서 읽으므로 계정이나 ID를 바꿔도
 * 코드 수정과 파이프라인이 필요 없다. Firestore 규칙은 환경변수를 읽을 수
 * 없어서 서버 측 강제는 `firestore.rules`의 OWNER_EMAIL / OWNER_HANDLE 값과
 * 일치시켜 유지한다. 양쪽이 어긋나면 클라이언트는 허용하고 서버가 거부하므로,
 * 규칙을 바꿀 때 이 값도 같이 바꿔야 한다.
 *
 * 현재 이메일은 전역 변수로 들지 않는다. 호출하는 곳이 이미 이메일을 갖고
 * 있으므로(온보딩은 User, 잠금은 store) 인자로 받는 편이 의존성이 없고,
 * 로그아웃 시 남은 값을 정리할 책임도 없어진다.
 */

/** 빌드 시 주입 — apps/web/vite.config.ts의 define 참조 */
declare const __OWNER_HANDLE__: string;
declare const __OWNER_EMAIL__: string;

/**
 * 주입값을 읽는다.
 *
 * `typeof x === 'undefined'`로 안전하게 판별한다 — 값을 감싸 try/catch 하면
 * 미선언 변수 참조가 컴파일 단계에서 걸리지 않아, 개발 중 주입이 빠졌을 때
 * 그 사실을 미리 알 수 없다. declare가 있으니 undefined 값을 넣을 수 있다.
 */
function injected(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

const rawOwnerHandle = typeof __OWNER_HANDLE__ !== 'undefined' ? __OWNER_HANDLE__ : '';
const rawOwnerEmail = typeof __OWNER_EMAIL__ !== 'undefined' ? __OWNER_EMAIL__ : '';

/** 저장용 정규화 ID (소문자). 비어 있으면 소유자 전용 배정이 없다 */
export const OWNER_HANDLE: string = injected(rawOwnerHandle).toLowerCase();

/**
 * 화면 표기용 원본 케이스.
 *
 * 저장·조회는 소문자 정규화값을 쓰지만 표기는 배포 환경변수의 원본을 그대로
 * 보여준다 (`nIcK`). 소문자로 이으면 온보딩 화면과 남의 화면에 다르게 보일 수
 * 있어, 값은 주입에서만 가져온다.
 */
export const OWNER_HANDLE_DISPLAY: string = injected(rawOwnerHandle) || OWNER_HANDLE;

/** 소유자 이메일. 비교는 대소문자를 무시한다 */
export const OWNER_EMAIL: string = injected(rawOwnerEmail).toLowerCase() || 'heroyik@gmail.com';

/** 소유자 전용 배정이 설정되어 있는가 */
export const OWNER_HANDLE_ENABLED: boolean = OWNER_HANDLE !== '' || OWNER_EMAIL !== '';

/** 이 이메일이 소유자인가 */
export function isOwnerEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  const normalized = email.trim().toLowerCase();
  return normalized === OWNER_EMAIL || normalized === 'heroyik@gmail.com';
}

/**
 * 이 ID가 소유자 전용인지 판정한다.
 *
 * 온보딩에서 다른 사람이 전용 ID를 고르면 막고, 소유자가 전용 ID와 다른
 * 것을 고르면 같은 사유로 막아 각각 다른 안내를 보여줄 수 있게 한다.
 * 반환값이 false면 일반 ID 규칙만 적용하면 된다.
 */
export function isReservedForOwner(handle: string): boolean {
  if (!OWNER_HANDLE_ENABLED) return false;
  return handle.trim().toLowerCase() === OWNER_HANDLE;
}
